import React, { useEffect, useMemo, useState, Component } from 'react';
import { Truck, Package, CheckCircle, RefreshCw, Building2, ChevronDown, Search, User, MapPin, PackageCheck, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../contexts/AuthContext';
import { useOrders } from '../contexts/OrderContext';
import { filterTodayOrders } from '../utils/dateFilters';
import { displayProductName } from '../utils/formatters';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
type OrderStatus = 'Pendiente' | 'En Preparación' | 'Listo' | 'Entregado' | 'Cancelado';
const STATUS_FLOW: OrderStatus[] = ['Pendiente', 'En Preparación', 'Listo', 'Entregado'];
interface AssignedBranch {
  companyId: string | number;
  companyName: string;
  branchId: string;
  branchName: string;
}
export function DeliveryDashboard() {
  useDocumentTitle('Mis Entregas');
  const {
    user
  } = useAuth();
  const {
    orders,
    updateOrderStatus,
    refreshOrders
  } = useOrders();
  const {
    showToast
  } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'Activos' | 'Listos' | 'Entregados' | 'Todos'>('Activos');
  const [statusChanging, setStatusChanging] = useState<string | null>(null);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  // Live assignments state
  const [assignedBranches, setAssignedBranches] = useState<AssignedBranch[]>([]);
  // Determine if user is a regular admin (not delivery-only)
  const isFullAdmin = user?.role === 'admin' && !user?.isDeliveryOnly;
  // Load fresh assignments from DB
  useEffect(() => {
    const loadAssignments = async () => {
      if (isFullAdmin) return;
      if (!user?.id || !isSupabaseConfigured()) return;
      try {
        const adminUsers = await supabaseClient.getAdminUsers();
        const currentUser = adminUsers?.find((u: any) => u.id === user.id);
        if (currentUser?.permissions?.assignedBranches) {
          setAssignedBranches(currentUser.permissions.assignedBranches);
        } else {
          // Fallback to session data if DB fetch fails or is empty
          setAssignedBranches((user as any).permissions?.assignedBranches || []);
        }
      } catch (e) {
        console.error('Error loading assignments:', e);
      }
    };
    loadAssignments();
    // Poll for assignment changes every 15s
    const interval = setInterval(loadAssignments, 15000);
    return () => clearInterval(interval);
  }, [user?.id, isFullAdmin]);
  // Auto-refresh orders
  useEffect(() => {
    const interval = setInterval(() => {
      refreshOrders?.();
    }, 10000);
    return () => clearInterval(interval);
  }, [refreshOrders]);
  // --- CORE FILTERING LOGIC ---
  const allMyOrders = useMemo(() => {
    const todayOrders = filterTodayOrders(orders);
    // 1. Full Admin sees everything
    if (isFullAdmin) {
      return todayOrders;
    }
    // 2. Delivery User: Filter by Assigned Branches
    if (assignedBranches.length === 0) {
      return [];
    }
    return todayOrders.filter(order => {
      // Check if this order belongs to any assigned branch
      return assignedBranches.some(assignment => {
        const companyMatch = assignment.companyName === order.company;
        // Branch match logic:
        // - Exact match
        // - OR assignment is 'Principal' and order branch is falsy (null/empty)
        // - OR assignment is 'Principal' and order branch is 'Principal'
        const branchMatch = assignment.branchName === order.branch || assignment.branchName === 'Principal' && !order.branch || assignment.branchName === 'Principal' && order.branch === 'Principal';
        return companyMatch && branchMatch;
      });
    });
  }, [orders, assignedBranches, isFullAdmin]);
  // Apply UI filters (search + status tab)
  const filteredOrders = useMemo(() => {
    return allMyOrders.filter(order => {
      // Status filter
      if (activeTab === 'Activos') {
        if (order.status === 'Entregado' || order.status === 'Cancelado') return false;
      } else if (activeTab === 'Listos') {
        if (order.status !== 'Listo') return false;
      } else if (activeTab === 'Entregados') {
        if (order.status !== 'Entregado') return false;
      }
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return order.name.toLowerCase().includes(term) || order.orderNo.includes(term) || order.product.toLowerCase().includes(term) || order.company.toLowerCase().includes(term) || order.branch && order.branch.toLowerCase().includes(term);
      }
      return true;
    });
  }, [allMyOrders, searchTerm, activeTab]);
  // Group by company
  const companyGroupedOrders = useMemo(() => {
    const byCompany: Record<string, any[]> = {};
    // Sort helper
    const sortFn = (a: any, b: any) => {
      const priority: Record<string, number> = {
        Listo: 0,
        'En Preparación': 1,
        Pendiente: 2,
        Entregado: 3,
        Cancelado: 4
      };
      return (priority[a.status] ?? 5) - (priority[b.status] ?? 5);
    };
    filteredOrders.forEach(order => {
      if (!byCompany[order.company]) byCompany[order.company] = [];
      byCompany[order.company].push(order);
    });
    // Sort within groups
    Object.values(byCompany).forEach(arr => arr.sort(sortFn));
    return byCompany;
  }, [filteredOrders]);
  // Auto-expand companies with orders
  useEffect(() => {
    const companiesWithOrders = Object.keys(companyGroupedOrders);
    if (companiesWithOrders.length > 0 && expandedCompanies.size === 0) {
      setExpandedCompanies(new Set(companiesWithOrders));
    }
  }, [companyGroupedOrders]);
  const toggleCompanyExpand = (companyName: string) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyName)) {
        next.delete(companyName);
      } else {
        next.add(companyName);
      }
      return next;
    });
  };
  // Stats
  const stats = useMemo(() => {
    const activeOrders = allMyOrders.filter(o => o.status !== 'Cancelado');
    const total = activeOrders.length;
    const delivered = activeOrders.filter(o => o.status === 'Entregado').length;
    const ready = activeOrders.filter(o => o.status === 'Listo').length;
    const pending = activeOrders.filter(o => o.status === 'Pendiente' || o.status === 'En Preparación').length;
    const progress = total > 0 ? Math.round(delivered / total * 100) : 0;
    return {
      total,
      delivered,
      ready,
      pending,
      progress
    };
  }, [allMyOrders]);
  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    setStatusChanging(orderId);
    try {
      await updateOrderStatus(orderId, newStatus);
      showToast('success', 'Actualizado', `Estado cambiado a "${newStatus}"`);
    } catch (error: any) {
      showToast('error', 'Error', error.message || 'No se pudo actualizar el estado');
    } finally {
      setStatusChanging(null);
    }
  };
  const getNextStatus = (currentStatus: OrderStatus): OrderStatus | null => {
    const idx = STATUS_FLOW.indexOf(currentStatus);
    if (idx === -1 || idx >= STATUS_FLOW.length - 1) return null;
    return STATUS_FLOW[idx + 1];
  };
  const handleDeliverAllReady = async (orders: any[]) => {
    const readyOrders = orders.filter(o => o.status === 'Listo');
    if (readyOrders.length === 0) return;
    if (!window.confirm(`¿Marcar ${readyOrders.length} pedidos como Entregados?`)) return;
    for (const order of readyOrders) {
      handleStatusChange(order.id, 'Entregado');
    }
  };
  // --- Components ---
  const DeliveryCard = ({
    order
  }: {
    order: any;
  }) => {
    const nextStatus = getNextStatus(order.status as OrderStatus);
    const isChanging = statusChanging === order.id;
    const isDelivered = order.status === 'Entregado';
    return <div className={`
        bg-white dark:bg-gray-900 rounded-2xl p-4 shadow-sm border transition-all relative overflow-hidden
        ${isDelivered ? 'border-gray-200 dark:border-gray-800 opacity-75' : 'border-gray-200 dark:border-gray-800 hover:shadow-md'}
      `} data-id="element-2463">
        <div className="flex justify-between items-start mb-3 pl-1" data-id="element-2464">
          <div data-id="element-2465">
            <div className="flex items-center gap-2 mb-1" data-id="element-2466">
              <span className="font-mono text-sm font-bold text-gray-500 dark:text-gray-400" data-id="element-2467">
                #{order.orderNo}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${order.status === 'Listo' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : order.status === 'En Preparación' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : order.status === 'Entregado' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`} data-id="element-2468">
                {order.status}
              </span>
            </div>
            <h3 className="font-bold text-lg text-gray-900 dark:text-white leading-tight" data-id="element-2469">
              {order.name}
            </h3>
          </div>
          <div className="text-right" data-id="element-2470">
            <span className="text-xs font-mono text-gray-400" data-id="element-2471">
              {order.orderDate.split(',')[1]?.trim().slice(0, 5)}
            </span>
          </div>
        </div>

        <div className="pl-1 mb-4 space-y-2" data-id="element-2472">
          <div className="bg-gray-50 dark:bg-gray-800/50 p-2.5 rounded-xl" data-id="element-2473">
            <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2" data-id="element-2474">
              {displayProductName(order.product)}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-2475">
            <Building2 size={12} data-id="element-2476" />
            <span className="font-medium" data-id="element-2477">{order.company}</span>
            <span className="text-gray-300 dark:text-gray-700" data-id="element-2478">•</span>
            <MapPin size={12} data-id="element-2479" />
            <span data-id="element-2480">{order.branch || 'Principal'}</span>
          </div>
        </div>

        {nextStatus && order.status !== 'Cancelado' && <Button fullWidth onClick={() => handleStatusChange(order.id, nextStatus)} disabled={isChanging} className={`
              h-12 rounded-xl text-sm font-bold shadow-sm
              ${nextStatus === 'Entregado' ? 'bg-green-600 hover:bg-green-700 text-white shadow-green-600/20' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'}
            `} data-id="element-2481">
            {isChanging ? <span className="flex items-center gap-2" data-id="element-2482">
                <RefreshCw className="animate-spin" size={16} data-id="element-2483" /> Procesando...
              </span> : nextStatus === 'Entregado' ? <span className="flex items-center gap-2" data-id="element-2484">
                <PackageCheck size={18} data-id="element-2485" /> Marcar Entregado
              </span> : <span className="flex items-center gap-2" data-id="element-2486">
                Mover a {nextStatus} <ArrowRight size={16} data-id="element-2487" />
              </span>}
          </Button>}
      </div>;
  };
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-24" data-id="element-2488">
      {/* Header & Stats Strip */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-30 shadow-sm" data-id="element-2489">
        <div className="p-4 max-w-5xl mx-auto" data-id="element-2490">
          <div className="flex justify-between items-center mb-4" data-id="element-2491">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-2492">
              <Truck className="text-blue-600" size={24} data-id="element-2493" />
              Mis Entregas
            </h1>
            <button onClick={() => refreshOrders?.()} className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-2494">
              <RefreshCw size={18} data-id="element-2495" />
            </button>
          </div>

          {/* Horizontal Stats Scroll */}
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-2496">
            <div className="flex-shrink-0 min-w-[100px] p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-2497">
              <p className="text-[10px] uppercase text-gray-500 font-bold mb-1" data-id="element-2498">
                Total
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2499">
                {stats.total}
              </p>
            </div>
            <div className="flex-shrink-0 min-w-[100px] p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-100 dark:border-yellow-800" data-id="element-2500">
              <p className="text-[10px] uppercase text-yellow-700 dark:text-yellow-400 font-bold mb-1" data-id="element-2501">
                Pendientes
              </p>
              <p className="text-2xl font-bold text-yellow-800 dark:text-yellow-300" data-id="element-2502">
                {stats.pending}
              </p>
            </div>
            <div className="flex-shrink-0 min-w-[100px] p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800" data-id="element-2503">
              <p className="text-[10px] uppercase text-green-700 dark:text-green-400 font-bold mb-1" data-id="element-2504">
                Listos
              </p>
              <p className="text-2xl font-bold text-green-800 dark:text-green-300" data-id="element-2505">
                {stats.ready}
              </p>
            </div>
            <div className="flex-shrink-0 min-w-[100px] p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800" data-id="element-2506">
              <p className="text-[10px] uppercase text-blue-700 dark:text-blue-400 font-bold mb-1" data-id="element-2507">
                Entregados
              </p>
              <p className="text-2xl font-bold text-blue-800 dark:text-blue-300" data-id="element-2508">
                {stats.delivered}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-2509">
            <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{
            width: `${stats.progress}%`
          }} data-id="element-2510"></div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-6" data-id="element-2511">
        {/* Controls */}
        <div className="space-y-4" data-id="element-2512">
          <div className="relative" data-id="element-2513">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2514" />
            <input type="text" placeholder="Buscar pedido o cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-base" data-id="element-2515" />
          </div>

          {/* Tabs */}
          <div className="flex p-1 bg-gray-200 dark:bg-gray-800 rounded-xl overflow-x-auto no-scrollbar" data-id="element-2516">
            {['Activos', 'Listos', 'Entregados', 'Todos'].map(tab => <button key={tab} onClick={() => setActiveTab(tab as any)} className={`
                  flex-1 py-2 px-4 rounded-lg text-sm font-bold whitespace-nowrap transition-all
                  ${activeTab === tab ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}
                `} data-id="element-2517">
                {tab}
              </button>)}
          </div>
        </div>

        {/* Company Groups */}
        {Object.entries(companyGroupedOrders).map(([companyName, companyOrders]) => {
        const isExpanded = expandedCompanies.has(companyName);
        const readyCount = companyOrders.filter(o => o.status === 'Listo').length;
        return <div key={companyName} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden" data-id="element-2518">
                <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" onClick={() => toggleCompanyExpand(companyName)} data-id="element-2519">
                  <div className="flex items-center gap-3" data-id="element-2520">
                    <div className={`p-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-2521">
                      <ChevronDown size={16} className="text-gray-500" data-id="element-2522" />
                    </div>
                    <div data-id="element-2523">
                      <h3 className="font-bold text-gray-900 dark:text-white text-base" data-id="element-2524">
                        {companyName}
                      </h3>
                      <p className="text-xs text-gray-500" data-id="element-2525">
                        {companyOrders.length} pedidos
                      </p>
                    </div>
                  </div>

                  {readyCount > 0 && <button onClick={e => {
              e.stopPropagation();
              handleDeliverAllReady(companyOrders);
            }} className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-bold rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors" data-id="element-2526">
                      Entregar {readyCount} Listos
                    </button>}
                </div>

                <AnimatePresence data-id="element-2527">
                  {isExpanded && <motion.div initial={{
              height: 0
            }} animate={{
              height: 'auto'
            }} exit={{
              height: 0
            }} className="overflow-hidden" data-id="element-2528">
                      <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-950/50" data-id="element-2529">
                        <div className="h-2 md:hidden" data-id="element-2530"></div> {/* Spacer */}
                        {companyOrders.map(order => <DeliveryCard key={order.id} order={order} data-id="element-2531" />)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
      })}

        {filteredOrders.length === 0 && <div className="text-center py-20 opacity-60" data-id="element-2532">
            <Package size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-2533" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2534">
              No hay pedidos
            </h3>
            <p className="text-gray-500" data-id="element-2535">
              No se encontraron pedidos con los filtros actuales.
            </p>
          </div>}
      </div>
    </div>;
}